import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { RefreshCw, Send, CheckCircle, XCircle, Globe, Link, ExternalLink, Search } from 'lucide-react';

const IndexNowAdmin = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [customUrl, setCustomUrl] = useState('');
  const [gscStatus, setGscStatus] = useState(null);
  const [gscLoading, setGscLoading] = useState(false);
  const [gscSubmitting, setGscSubmitting] = useState(false);
  const [gscResult, setGscResult] = useState(null);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/indexnow/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Error fetching IndexNow status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchGscStatus();
  }, []);

  const fetchGscStatus = async () => {
    setGscLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/gsc-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGscStatus(data);
      }
    } catch (error) {
      console.error('Error fetching GSC status:', error);
    } finally {
      setGscLoading(false);
    }
  };

  const submitToGoogle = async () => {
    setGscSubmitting(true);
    setGscResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/gsc-submit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setGscResult(data);
      // refresh status after submit
      fetchGscStatus();
    } catch (error) {
      setGscResult({ error: error.message });
    } finally {
      setGscSubmitting(false);
    }
  };

  const submitAllPages = async () => {
    setSubmitting(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/indexnow/submit-all-pages`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setResult(data);
    } catch (error) {
      setResult({ success: false, message: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const submitCustomUrl = async () => {
    if (!customUrl.trim()) return;
    
    setSubmitting(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/indexnow/submit-single`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: customUrl })
      });
      const data = await response.json();
      setResult(data);
      if (data.success) setCustomUrl('');
    } catch (error) {
      setResult({ success: false, message: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 text-center">
        <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-500" />
        <p className="mt-2">Loading IndexNow status...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-blue-100 rounded-xl">
          <Globe className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-bold">IndexNow (Bing)</h2>
          <p className="text-gray-600 text-sm">Instant search engine indexing</p>
        </div>
      </div>

      {/* Status Card */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-6 border border-blue-200">
        <h3 className="font-bold mb-3 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-500" />
          IndexNow is Configured
        </h3>
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-600">Host:</span>
            <span className="ml-2 font-mono bg-white px-2 py-1 rounded">{status?.host}</span>
          </div>
          <div>
            <span className="text-gray-600">API Key:</span>
            <span className="ml-2 font-mono bg-white px-2 py-1 rounded text-xs">{status?.key?.substring(0, 12)}...</span>
          </div>
        </div>
        <div className="mt-3">
          <a 
            href={status?.key_file_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline text-sm flex items-center gap-1"
          >
            <ExternalLink className="w-4 h-4" />
            Verify key file
          </a>
        </div>
      </div>

      {/* Submit All Pages */}
      <div className="bg-gray-50 rounded-xl p-4 mb-6">
        <h3 className="font-bold mb-3">🚀 Submit All Pages</h3>
        <p className="text-sm text-gray-600 mb-4">
          Notify Bing about all your main website pages. Use this after major content updates.
        </p>
        <Button 
          onClick={submitAllPages}
          disabled={submitting}
          className="bg-blue-500 hover:bg-blue-600 gap-2"
        >
          {submitting ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {submitting ? 'Submitting...' : 'Submit All Pages to Bing'}
        </Button>
      </div>

      {/* Submit Custom URL */}
      <div className="bg-gray-50 rounded-xl p-4 mb-6">
        <h3 className="font-bold mb-3">🔗 Submit Specific URL</h3>
        <p className="text-sm text-gray-600 mb-4">
          Submit a specific page URL for immediate indexing.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 relative">
            <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="url"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              placeholder="https://1staveicp.com/menu"
              className="w-full pl-10 pr-4 py-2 rounded-lg border-2 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <Button 
            onClick={submitCustomUrl}
            disabled={submitting || !customUrl.trim()}
            className="bg-purple-500 hover:bg-purple-600 gap-2"
          >
            <Send className="w-4 h-4" />
            Submit
          </Button>
        </div>
      </div>

      {/* Result Display */}
      {result && (
        <div className={`rounded-xl p-4 ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            {result.success ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500" />
            )}
            <span className={`font-bold ${result.success ? 'text-green-700' : 'text-red-700'}`}>
              {result.success ? 'Success!' : 'Error'}
            </span>
          </div>
          <p className={`text-sm ${result.success ? 'text-green-600' : 'text-red-600'}`}>
            {result.message}
          </p>
          {result.urls && result.urls.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-1">Submitted URLs:</p>
              <ul className="text-xs font-mono bg-white rounded p-2 max-h-32 overflow-y-auto">
                {result.urls.map((url, i) => (
                  <li key={i} className="truncate">{url}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Google Search Console */}
      <div className="mt-6 border-t pt-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-red-100 rounded-xl">
            <Search className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold">Google Search Console</h2>
            <p className="text-gray-600 text-sm">Submit sitemap directly to Google</p>
          </div>
        </div>

        {/* GSC Status */}
        {gscLoading ? (
          <div className="bg-gray-50 rounded-xl p-4 mb-4 text-center text-sm text-gray-600">
            <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
            Loading Google Search Console status...
          </div>
        ) : gscStatus?.sitemaps?.length > 0 ? (
          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 mb-4 border border-red-200">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Sitemap Status
            </h3>
            {gscStatus.sitemaps.map((sm, i) => (
              <div key={i} className="text-sm space-y-1" data-testid={`gsc-sitemap-row-${i}`}>
                {sm.error ? (
                  <div className="text-red-600">{sm.error}</div>
                ) : (
                  <>
                    <div className="font-mono text-xs bg-white rounded px-2 py-1 inline-block">{sm.path}</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                      <div><span className="text-gray-500">Last submitted:</span> <br /><span className="font-medium">{sm.lastSubmitted ? new Date(sm.lastSubmitted).toLocaleDateString() : '—'}</span></div>
                      <div><span className="text-gray-500">Last downloaded:</span> <br /><span className="font-medium">{sm.lastDownloaded ? new Date(sm.lastDownloaded).toLocaleDateString() : '—'}</span></div>
                      <div><span className="text-gray-500">Errors:</span> <br /><span className={`font-medium ${Number(sm.errors) > 0 ? 'text-red-600' : 'text-green-600'}`}>{sm.errors}</span></div>
                      <div><span className="text-gray-500">Warnings:</span> <br /><span className={`font-medium ${Number(sm.warnings) > 0 ? 'text-amber-600' : 'text-green-600'}`}>{sm.warnings}</span></div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        ) : gscStatus?.error ? (
          <div className="bg-red-50 rounded-xl p-4 mb-4 border border-red-200 text-sm text-red-700">
            {gscStatus.error}
          </div>
        ) : null}

        <div className="bg-gray-50 rounded-xl p-4">
          <h3 className="font-bold mb-3">🚀 Submit Sitemap to Google</h3>
          <p className="text-sm text-gray-600 mb-4">
            Re-submit your sitemap.xml to Google Search Console. Use this after publishing new pages, QR pages, specials, or menu items to request faster re-crawl.
          </p>
          <Button
            onClick={submitToGoogle}
            disabled={gscSubmitting}
            className="bg-red-500 hover:bg-red-600 gap-2"
            data-testid="gsc-submit-btn"
          >
            {gscSubmitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {gscSubmitting ? 'Submitting...' : 'Submit to Google'}
          </Button>

          {gscResult && (
            <div className={`mt-4 rounded-xl p-3 text-sm ${gscResult.error || gscResult.results?.some(r => r.error) ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'}`}>
              {gscResult.error ? (
                <div>{gscResult.error}</div>
              ) : (
                <ul className="space-y-1">
                  {gscResult.results?.map((r, i) => (
                    <li key={i} className="flex items-center gap-2" data-testid={`gsc-submit-result-${i}`}>
                      {r.error ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      <span className="font-mono text-xs">{r.sitemap}</span>
                      <span>— {r.error || r.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
        <h4 className="font-bold text-yellow-800 mb-2">ℹ️ How IndexNow Works</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• Instantly notifies Bing when your content changes</li>
          <li>• Pages are typically crawled within minutes, not days</li>
          <li>• Use after updating menu items, specials, or page content</li>
          <li>• No daily limits for legitimate content updates</li>
        </ul>
      </div>
    </div>
  );
};

export default IndexNowAdmin;
